import { Container, Graphics, Text, TextStyle, Sprite, Texture } from 'pixi.js-legacy';
import { CONFIG } from '../config';
import { GameState } from '../core/Game';
import { getAllAchievements } from '../data/achievements';
import type { Achievement } from '../data/achievements';
import { PixiRenderer } from '../pixi/PixiRenderer';
import { BackgroundRenderer } from '../pixi/ui/BackgroundRenderer';

interface InputManagerLike {
    getAxis(axis: string): number;
    isButtonPressed(button: string): boolean;
}

interface AchievementManagerLike {
    isUnlocked(id: string): boolean;
    getProgress(): string;
}

interface BackgroundState {
    hue: number;
    angle: number;
}

interface GameLike {
    getRenderer(): PixiRenderer;
    getInputManager(): InputManagerLike;
    getAchievementManager(): AchievementManagerLike;
    getBackgroundState(): BackgroundState;
    setState(state: GameState): void;
}

export class AchievementScreen {
    private game: GameLike;
    private starAngle: number;

    // Scroll state
    private scrollY: number;
    private maxScrollY: number;
    private isDragging: boolean;
    private lastTouchY: number;
    private velocity: number;
    private dragStartY: number;

    // Selection state
    private selectedIndex: number;
    private selectedAchievement: Achievement | null;

    // Image cache
    private imageCache: Record<string, HTMLImageElement>;
    private loadedImages: Set<string>;
    private textureCache: Map<string, Texture>;

    // Image fade state
    private imageAlpha: number;
    private targetImageAlpha: number;
    private currentImageId: string | null;

    // Input handlers
    private clickHandler: ((e: MouseEvent) => void) | null;
    private touchStartHandler: ((e: TouchEvent) => void) | null;
    private touchMoveHandler: ((e: TouchEvent) => void) | null;
    private touchEndHandler: ((e: TouchEvent) => void) | null;
    private wheelHandler: ((e: WheelEvent) => void) | null;

    // Layout constants
    private itemHeight: number;
    private listStartY: number;
    private listEndY: number;
    private listWidth: number;
    private lastIsLandscape: boolean | null;

    // Other state
    private ignoreNextTouchEnd: boolean;
    private keyRepeatDelay: number;
    private keyRepeatInterval: number;
    private upHoldTime: number;
    private downHoldTime: number;
    private upRepeating: boolean;
    private downRepeating: boolean;
    private backSelected: boolean;

    // PixiJS
    private pixiRenderer: PixiRenderer | null = null;
    private container: Container | null = null;
    private backgroundRenderer: BackgroundRenderer | null = null;
    private titleText: Text | null = null;
    private progressText: Text | null = null;
    private backText: Text | null = null;
    private starGraphics: Graphics | null = null;
    private listContainer: Container | null = null;
    private listMask: Graphics | null = null;
    private scrollBar: Graphics | null = null;
    private imageSprite: Sprite | null = null;
    private achievementTexts: { name: Text; desc: Text; bg: Graphics }[] = [];

    constructor(game: GameLike) {
        this.game = game;
        this.starAngle = 0;

        this.scrollY = 0;
        this.maxScrollY = 0;
        this.isDragging = false;
        this.lastTouchY = 0;
        this.velocity = 0;
        this.dragStartY = 0;

        this.selectedIndex = -1;
        this.selectedAchievement = null;

        this.imageCache = {};
        this.loadedImages = new Set();
        this.textureCache = new Map();

        this.imageAlpha = 0;
        this.targetImageAlpha = 0;
        this.currentImageId = null;

        this.clickHandler = null;
        this.touchStartHandler = null;
        this.touchMoveHandler = null;
        this.touchEndHandler = null;
        this.wheelHandler = null;

        this.itemHeight = 75;
        this.listStartY = 170;
        this.listEndY = 0;
        this.listWidth = 0;
        this.lastIsLandscape = null;

        this.ignoreNextTouchEnd = false;
        this.keyRepeatDelay = 0.4;
        this.keyRepeatInterval = 0.1;
        this.upHoldTime = 0;
        this.downHoldTime = 0;
        this.upRepeating = false;
        this.downRepeating = false;
        this.backSelected = false;
    }

    setPixiRenderer(renderer: PixiRenderer): void {
        this.pixiRenderer = renderer;
    }

    init(): void {
        this.starAngle = 0;
        this.scrollY = 0;
        this.velocity = 0;
        this.selectedIndex = -1;
        this.selectedAchievement = null;
        this.lastIsLandscape = null;
        this.ignoreNextTouchEnd = true;

        this.imageAlpha = 0;
        this.targetImageAlpha = 0;
        this.currentImageId = null;

        this.keyRepeatDelay = 0.4;
        this.keyRepeatInterval = 0.1;
        this.upHoldTime = 0;
        this.downHoldTime = 0;
        this.upRepeating = false;
        this.downRepeating = false;
        this.backSelected = false;

        this.updateLayout();
        this.preloadImages();
        this.setupEventListeners();
        this.initPixiUI();
    }

    updateLayout(): void {
        const renderer = this.game.getRenderer();
        const height = renderer.getCanvasHeight();
        const width = renderer.getCanvasWidth();
        const isLandscape = renderer.isLandscape;

        if (isLandscape) {
            this.listStartY = 130;
            this.listEndY = height - 80;
            this.listWidth = width * 0.5;
        } else {
            this.listStartY = height * 0.5;
            this.listEndY = height - 80;
            this.listWidth = width;
        }

        const listHeight = this.listEndY - this.listStartY;
        const contentHeight = getAllAchievements().length * this.itemHeight;
        this.maxScrollY = Math.max(0, contentHeight - listHeight);

        this.scrollY = Math.max(0, Math.min(this.maxScrollY, this.scrollY));

        this.lastIsLandscape = isLandscape;
    }

    preloadImages(): void {
        const achievements = getAllAchievements();
        this.loadImage('locked');
        achievements.forEach(achievement => {
            this.loadImage(achievement.id);
        });
    }

    loadImage(id: string): void {
        if (this.loadedImages.has(id)) return;

        const img = new Image();
        img.onload = () => {
            this.imageCache[id] = img;
            this.loadedImages.add(id);
        };
        img.onerror = () => {
            if (img.src.endsWith('.png')) {
                img.src = `assets/achievements/${id}.svg`;
            } else {
                console.warn(`Failed to load achievement image: ${id}`);
            }
        };
        img.src = `assets/achievements/${id}.png`;
    }

    setupEventListeners(): void {
        const canvas = this.pixiRenderer?.getCanvas() || document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!canvas) return;

        this.clickHandler = (e: MouseEvent) => this.handleClick(e);
        this.touchStartHandler = (e: TouchEvent) => this.handleTouchStart(e);
        this.touchMoveHandler = (e: TouchEvent) => this.handleTouchMove(e);
        this.touchEndHandler = (e: TouchEvent) => this.handleTouchEnd(e);
        this.wheelHandler = (e: WheelEvent) => this.handleWheel(e);

        canvas.addEventListener('click', this.clickHandler);
        canvas.addEventListener('touchstart', this.touchStartHandler, { passive: false });
        canvas.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
        canvas.addEventListener('touchend', this.touchEndHandler);
        canvas.addEventListener('wheel', this.wheelHandler, { passive: false });
    }

    private initPixiUI(): void {
        if (!this.pixiRenderer) return;

        const layers = this.pixiRenderer.getLayers();
        this.container = new Container();
        layers.ui.addChild(this.container);

        // Background
        this.backgroundRenderer = new BackgroundRenderer(layers.background);

        const width = this.pixiRenderer.getCanvasWidth();
        const height = this.pixiRenderer.getCanvasHeight();
        const isLandscape = this.pixiRenderer.isLandscape;

        // Title
        const titleStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: CONFIG.UI.SUBTITLE_SIZE * 1.2,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'center',
        });
        this.titleText = new Text('ACHIEVEMENTS', titleStyle);
        this.titleText.anchor.set(0.5);
        this.titleText.position.set(isLandscape ? this.listWidth / 2 : width / 2, 35);
        this.container.addChild(this.titleText);

        // Progress
        const progressStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: CONFIG.UI.SUBTITLE_SIZE * 0.5,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'center',
        });
        this.progressText = new Text(this.game.getAchievementManager().getProgress(), progressStyle);
        this.progressText.anchor.set(0.5);
        this.progressText.position.set(isLandscape ? this.listWidth / 2 : width / 2, 100);
        this.container.addChild(this.progressText);

        // List container with mask
        this.listContainer = new Container();
        this.container.addChild(this.listContainer);

        this.listMask = new Graphics();
        this.listMask.beginFill(0xFFFFFF);
        this.listMask.drawRect(0, this.listStartY, isLandscape ? this.listWidth : width, this.listEndY - this.listStartY);
        this.listMask.endFill();
        this.container.addChild(this.listMask);
        this.listContainer.mask = this.listMask;

        // Create achievement list items
        this.createAchievementList();

        // Scroll bar
        this.scrollBar = new Graphics();
        this.container.addChild(this.scrollBar);

        // Image sprite for selected achievement (add before progress text so text is on top)
        this.imageSprite = new Sprite();
        this.imageSprite.anchor.set(0.5);
        this.imageSprite.visible = false;
        this.container.addChildAt(this.imageSprite, 0);  // Add at bottom of container

        // Back button
        const buttonStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: CONFIG.UI.SUBTITLE_SIZE * 0.7,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'center',
        });
        this.backText = new Text('BACK', buttonStyle);
        this.backText.anchor.set(0.5);
        const backX = isLandscape ? this.listWidth / 2 : width / 2;
        this.backText.position.set(backX, height - 35);
        this.container.addChild(this.backText);

        // Star graphics
        this.starGraphics = new Graphics();
        this.container.addChild(this.starGraphics);
    }

    private createAchievementList(): void {
        if (!this.listContainer) return;

        const achievements = getAllAchievements();
        const achievementManager = this.game.getAchievementManager();

        const nameStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: CONFIG.UI.SCORE_SIZE * 0.8,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'left',
        });
        const descStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: CONFIG.UI.SCORE_SIZE * 0.5,
            fontWeight: '100',
            fill: 'rgba(255, 255, 255, 0.6)',
            align: 'left',
        });

        this.achievementTexts = [];

        achievements.forEach((achievement, index) => {
            const y = this.listStartY + index * this.itemHeight + 30;
            const isUnlocked = achievementManager.isUnlocked(achievement.id);

            // Background highlight
            const bg = new Graphics();
            this.listContainer!.addChild(bg);

            // Name text
            const nameText = new Text(
                isUnlocked ? `★ ${achievement.name}` : '-----',
                nameStyle.clone()
            );
            nameText.anchor.set(0, 0.5);
            nameText.position.set(50, y);
            this.listContainer!.addChild(nameText);

            // Description text
            const descText = new Text(
                isUnlocked ? achievement.description : '-----',
                descStyle.clone()
            );
            descText.anchor.set(0, 0.5);
            descText.position.set(50, y + 32);
            this.listContainer!.addChild(descText);

            this.achievementTexts.push({ name: nameText, desc: descText, bg });
        });
    }

    handleClick(e: MouseEvent): void {
        const renderer = this.game.getRenderer();
        const target = e.target as HTMLElement;
        const rect = target.getBoundingClientRect();
        const scaleX = renderer.getCanvasWidth() / rect.width;
        const scaleY = renderer.getCanvasHeight() / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;
        const height = renderer.getCanvasHeight();
        const width = renderer.getCanvasWidth();
        const isLandscape = renderer.isLandscape;

        const backX = isLandscape ? this.listWidth / 2 : width / 2;
        if (y >= height - 60 && x >= backX - 80 && x <= backX + 80) {
            this.game.setState(GameState.TITLE);
            return;
        }

        const listX = isLandscape ? this.listWidth : width;
        if (x < listX && y >= this.listStartY && y <= this.listEndY) {
            const clickY = y - this.listStartY + this.scrollY;
            const index = Math.floor(clickY / this.itemHeight);
            const achievements = getAllAchievements();

            if (index >= 0 && index < achievements.length) {
                this.selectedIndex = index;
                this.selectedAchievement = achievements[index];
            }
        }
    }

    handleTouchStart(e: TouchEvent): void {
        if (e.touches.length === 1) {
            const renderer = this.game.getRenderer();
            const target = e.target as HTMLElement;
            const rect = target.getBoundingClientRect();
            const scaleX = renderer.getCanvasWidth() / rect.width;
            const scaleY = renderer.getCanvasHeight() / rect.height;
            const x = (e.touches[0].clientX - rect.left) * scaleX;
            const y = (e.touches[0].clientY - rect.top) * scaleY;
            const isLandscape = renderer.isLandscape;

            const listX = isLandscape ? this.listWidth : renderer.getCanvasWidth();

            if (x < listX && y >= this.listStartY && y <= this.listEndY) {
                this.isDragging = true;
                this.lastTouchY = y;
                this.dragStartY = y;
                this.velocity = 0;
                e.preventDefault();
            }
        }
    }

    handleTouchMove(e: TouchEvent): void {
        if (this.isDragging && e.touches.length === 1) {
            const renderer = this.game.getRenderer();
            const target = e.target as HTMLElement;
            const rect = target.getBoundingClientRect();
            const scaleY = renderer.getCanvasHeight() / rect.height;
            const y = (e.touches[0].clientY - rect.top) * scaleY;

            const deltaY = this.lastTouchY - y;
            this.scrollY = Math.max(0, Math.min(this.maxScrollY, this.scrollY + deltaY));
            this.velocity = deltaY;
            this.lastTouchY = y;
            e.preventDefault();
        }
    }

    handleTouchEnd(e: TouchEvent): void {
        if (this.ignoreNextTouchEnd) {
            this.ignoreNextTouchEnd = false;
            this.isDragging = false;
            return;
        }

        if (e.changedTouches.length > 0 && !this.isDragging) {
            const touch = e.changedTouches[0];
            const renderer = this.game.getRenderer();
            const target = e.target as HTMLElement;
            const rect = target.getBoundingClientRect();
            const scaleX = renderer.getCanvasWidth() / rect.width;
            const scaleY = renderer.getCanvasHeight() / rect.height;
            const x = (touch.clientX - rect.left) * scaleX;
            const y = (touch.clientY - rect.top) * scaleY;
            const width = renderer.getCanvasWidth();
            const height = renderer.getCanvasHeight();
            const isLandscape = renderer.isLandscape;

            const backX = isLandscape ? this.listWidth / 2 : width / 2;
            if (y >= height - 60 && x >= backX - 80 && x <= backX + 80) {
                this.game.setState(GameState.TITLE);
                return;
            }
        }

        if (this.isDragging) {
            const dragDistance = Math.abs(this.lastTouchY - this.dragStartY);
            if (dragDistance < 10) {
                const clickY = this.dragStartY - this.listStartY + this.scrollY;
                const index = Math.floor(clickY / this.itemHeight);
                const achievements = getAllAchievements();

                if (index >= 0 && index < achievements.length) {
                    this.selectedIndex = index;
                    this.selectedAchievement = achievements[index];
                }
            }
        }
        this.isDragging = false;
    }

    handleWheel(e: WheelEvent): void {
        const scrollAmount = e.deltaY * 0.5;
        this.scrollY = Math.max(0, Math.min(this.maxScrollY, this.scrollY + scrollAmount));
        e.preventDefault();
    }

    update(deltaTime: number): void {
        this.starAngle += deltaTime * 3;

        const renderer = this.game.getRenderer();
        if (this.lastIsLandscape !== renderer.isLandscape) {
            this.updateLayout();
        }

        if (!this.isDragging && Math.abs(this.velocity) > 0.1) {
            this.scrollY = Math.max(0, Math.min(this.maxScrollY, this.scrollY + this.velocity));
            this.velocity *= 0.95;
        }

        const input = this.game.getInputManager();
        const achievements = getAllAchievements();

        const upHeld = input.getAxis('vertical') < -0.5;
        const downHeld = input.getAxis('vertical') > 0.5;

        if (upHeld) {
            let shouldMove = false;
            if (this.upHoldTime === 0) {
                shouldMove = true;
            } else if (this.upHoldTime >= this.keyRepeatDelay) {
                if (!this.upRepeating) {
                    this.upRepeating = true;
                    this.upHoldTime = this.keyRepeatDelay;
                }
                if (this.upHoldTime >= this.keyRepeatDelay + this.keyRepeatInterval) {
                    shouldMove = true;
                    this.upHoldTime = this.keyRepeatDelay;
                }
            }
            this.upHoldTime += deltaTime;

            if (shouldMove) {
                if (this.backSelected) {
                    this.backSelected = false;
                    this.selectedIndex = achievements.length - 1;
                    this.selectedAchievement = achievements[this.selectedIndex];
                    this.ensureVisible(this.selectedIndex);
                } else if (this.selectedIndex > 0) {
                    this.selectedIndex--;
                    this.selectedAchievement = achievements[this.selectedIndex];
                    this.ensureVisible(this.selectedIndex);
                } else if (this.selectedIndex === -1) {
                    this.selectedIndex = 0;
                    this.selectedAchievement = achievements[0];
                    this.ensureVisible(0);
                }
            }
        } else {
            this.upHoldTime = 0;
            this.upRepeating = false;
        }

        if (downHeld) {
            let shouldMove = false;
            if (this.downHoldTime === 0) {
                shouldMove = true;
            } else if (this.downHoldTime >= this.keyRepeatDelay) {
                if (!this.downRepeating) {
                    this.downRepeating = true;
                    this.downHoldTime = this.keyRepeatDelay;
                }
                if (this.downHoldTime >= this.keyRepeatDelay + this.keyRepeatInterval) {
                    shouldMove = true;
                    this.downHoldTime = this.keyRepeatDelay;
                }
            }
            this.downHoldTime += deltaTime;

            if (shouldMove) {
                if (this.backSelected) {
                    // No further down
                } else if (this.selectedIndex >= achievements.length - 1) {
                    this.backSelected = true;
                    this.selectedIndex = -1;
                    this.selectedAchievement = null;
                } else if (this.selectedIndex === -1) {
                    this.selectedIndex = 0;
                    this.selectedAchievement = achievements[0];
                    this.ensureVisible(0);
                } else {
                    this.selectedIndex++;
                    this.selectedAchievement = achievements[this.selectedIndex];
                    this.ensureVisible(this.selectedIndex);
                }
            }
        } else {
            this.downHoldTime = 0;
            this.downRepeating = false;
        }

        if (input.isButtonPressed('confirm') && this.backSelected) {
            this.game.setState(GameState.TITLE);
        }

        if (input.isButtonPressed('cancel')) {
            this.game.setState(GameState.TITLE);
        }

        // Update image fade
        const fadeSpeed = 8; // 빠른 페이드
        if (this.imageAlpha < this.targetImageAlpha) {
            this.imageAlpha = Math.min(this.targetImageAlpha, this.imageAlpha + fadeSpeed * deltaTime);
        } else if (this.imageAlpha > this.targetImageAlpha) {
            this.imageAlpha = Math.max(this.targetImageAlpha, this.imageAlpha - fadeSpeed * deltaTime);
        }
    }

    ensureVisible(index: number): void {
        const itemTop = index * this.itemHeight;
        const itemBottom = itemTop + this.itemHeight;
        const viewTop = this.scrollY;
        const viewBottom = this.scrollY + (this.listEndY - this.listStartY);

        if (itemTop < viewTop) {
            this.scrollY = itemTop;
        } else if (itemBottom > viewBottom) {
            this.scrollY = itemBottom - (this.listEndY - this.listStartY);
        }
    }

    private drawRotatingStar(g: Graphics, x: number, y: number, size: number = 10): void {
        g.clear();
        g.lineStyle(2, 0xFFFFFF);
        g.position.set(x, y);
        g.rotation = this.starAngle;

        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            g.moveTo(0, 0);
            g.lineTo(Math.cos(angle) * size, Math.sin(angle) * size);
        }
    }

    render(): void {
        if (!this.pixiRenderer || !this.container) return;

        const renderer = this.game.getRenderer();
        const width = renderer.getCanvasWidth();
        const height = renderer.getCanvasHeight();
        const isLandscape = renderer.isLandscape;
        const bg = this.game.getBackgroundState();
        const achievementManager = this.game.getAchievementManager();

        // Update background
        if (this.backgroundRenderer) {
            this.backgroundRenderer.render(bg, width, height);
        }

        // Update title position
        if (this.titleText) {
            this.titleText.position.set(isLandscape ? this.listWidth / 2 : width / 2, 35);
        }

        // Update progress
        if (this.progressText) {
            this.progressText.text = achievementManager.getProgress();
            this.progressText.position.set(isLandscape ? this.listWidth / 2 : width / 2, 100);
        }

        // Update list mask
        if (this.listMask) {
            this.listMask.clear();
            this.listMask.beginFill(0xFFFFFF);
            this.listMask.drawRect(0, this.listStartY, isLandscape ? this.listWidth : width, this.listEndY - this.listStartY);
            this.listMask.endFill();
        }

        // Update achievement list
        const achievements = getAllAchievements();
        achievements.forEach((achievement, index) => {
            const item = this.achievementTexts[index];
            if (!item) return;

            const y = this.listStartY + index * this.itemHeight + 30 - this.scrollY;
            const isUnlocked = achievementManager.isUnlocked(achievement.id);
            const isSelected = index === this.selectedIndex;

            // Update positions
            item.name.position.set(50, y);
            item.desc.position.set(50, y + 32);

            // Update text content
            item.name.text = isUnlocked ? `★ ${achievement.name}` : '-----';
            item.desc.text = isUnlocked ? achievement.description : '-----';

            // Update colors/alpha
            if (isUnlocked) {
                item.name.style.fill = isSelected ? '#FFD700' : '#FFFFFF';
                item.desc.style.fill = isSelected ? 'rgba(255, 215, 0, 0.7)' : 'rgba(255, 255, 255, 0.6)';
            } else {
                item.name.style.fill = isSelected ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.4)';
                item.desc.style.fill = isSelected ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.3)';
            }

            // Update selection background
            item.bg.clear();
            if (isSelected) {
                item.bg.beginFill(0xFFFFFF, 0.2);
                item.bg.drawRect(0, y - 25, isLandscape ? this.listWidth : width, this.itemHeight);
                item.bg.endFill();
            }
        });

        // Update scroll bar
        if (this.scrollBar && this.maxScrollY > 0) {
            this.scrollBar.clear();
            const listHeight = this.listEndY - this.listStartY;
            const scrollBarHeight = listHeight * (listHeight / (this.maxScrollY + listHeight));
            const scrollBarY = this.listStartY + (this.scrollY / this.maxScrollY) * (listHeight - scrollBarHeight);
            const scrollBarX = isLandscape ? this.listWidth - 15 : width - 15;

            this.scrollBar.beginFill(0xFFFFFF, 0.3);
            this.scrollBar.drawRect(scrollBarX, scrollBarY, 8, scrollBarHeight);
            this.scrollBar.endFill();
        }

        // Update selected achievement image with fade
        if (this.imageSprite) {
            if (this.selectedAchievement) {
                const isUnlocked = achievementManager.isUnlocked(this.selectedAchievement.id);
                const imageId = isUnlocked ? this.selectedAchievement.id : 'locked';
                const img = this.imageCache[imageId];

                // 이미지가 변경되면 페이드아웃 후 페이드인
                if (this.currentImageId !== imageId) {
                    if (this.currentImageId === null) {
                        // 처음 선택: 바로 페이드인
                        this.currentImageId = imageId;
                        this.targetImageAlpha = 1;
                    } else {
                        // 이미지 변경: 페이드아웃 완료 후 변경
                        if (this.imageAlpha <= 0.01) {
                            this.currentImageId = imageId;
                            this.targetImageAlpha = 1;
                        } else {
                            this.targetImageAlpha = 0;
                        }
                    }
                } else {
                    this.targetImageAlpha = 1;
                }

                if (img && this.currentImageId) {
                    let imgX: number, imgY: number, imgSize: number;

                    if (isLandscape) {
                        const rightAreaWidth = width - this.listWidth;
                        imgSize = Math.min(rightAreaWidth * 0.85, height * 0.8);
                        imgX = this.listWidth + (rightAreaWidth) / 2;
                        imgY = height / 2;
                    } else {
                        const topAreaHeight = this.listStartY;
                        imgSize = Math.min(width * 0.65, (topAreaHeight - 70) * 0.9);
                        imgX = width / 2;
                        imgY = 90 + (topAreaHeight - 90) / 2;
                    }

                    // Get or create cached texture from image
                    let texture = this.textureCache.get(this.currentImageId);
                    if (!texture) {
                        const currentImg = this.imageCache[this.currentImageId];
                        if (currentImg) {
                            texture = Texture.from(currentImg);
                            this.textureCache.set(this.currentImageId, texture);
                        }
                    }
                    if (texture) {
                        this.imageSprite.texture = texture;
                        this.imageSprite.position.set(imgX, imgY);
                        this.imageSprite.width = imgSize;
                        this.imageSprite.height = imgSize;
                        this.imageSprite.alpha = this.imageAlpha;
                        this.imageSprite.visible = this.imageAlpha > 0.01;
                        // v7: BLEND_MODES.SCREEN = 3
                        this.imageSprite.blendMode = 3;
                    }
                } else {
                    this.imageSprite.visible = false;
                }
            } else {
                // 선택 해제: 페이드아웃
                this.targetImageAlpha = 0;
                if (this.imageAlpha <= 0.01) {
                    this.currentImageId = null;
                    this.imageSprite.visible = false;
                } else {
                    this.imageSprite.alpha = this.imageAlpha;
                    this.imageSprite.visible = true;
                }
            }
        }

        // Update back button
        if (this.backText) {
            const backX = isLandscape ? this.listWidth / 2 : width / 2;
            this.backText.position.set(backX, height - 35);
            this.backText.alpha = this.backSelected ? 1 : 0.5;
        }

        // Update star
        if (this.starGraphics) {
            if (this.backSelected && this.backText) {
                const starX = this.backText.x - this.backText.width / 2 - 25;
                this.drawRotatingStar(this.starGraphics, starX, height - 35);
                this.starGraphics.visible = true;
            } else {
                this.starGraphics.visible = false;
            }
        }
    }

    cleanup(): void {
        const canvas = this.pixiRenderer?.getCanvas() || document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (canvas) {
            if (this.clickHandler) {
                canvas.removeEventListener('click', this.clickHandler);
                this.clickHandler = null;
            }
            if (this.touchStartHandler) {
                canvas.removeEventListener('touchstart', this.touchStartHandler);
                this.touchStartHandler = null;
            }
            if (this.touchMoveHandler) {
                canvas.removeEventListener('touchmove', this.touchMoveHandler);
                this.touchMoveHandler = null;
            }
            if (this.touchEndHandler) {
                canvas.removeEventListener('touchend', this.touchEndHandler);
                this.touchEndHandler = null;
            }
            if (this.wheelHandler) {
                canvas.removeEventListener('wheel', this.wheelHandler);
                this.wheelHandler = null;
            }
        }

        if (this.backgroundRenderer) {
            this.backgroundRenderer.destroy();
            this.backgroundRenderer = null;
        }

        if (this.container) {
            this.container.destroy({ children: true });
            this.container = null;
        }

        this.titleText = null;
        this.progressText = null;
        this.backText = null;
        this.starGraphics = null;
        this.listContainer = null;
        this.listMask = null;
        this.scrollBar = null;
        this.imageSprite = null;
        this.achievementTexts = [];

        // Clean up texture cache
        this.textureCache.forEach(texture => texture.destroy(true));
        this.textureCache.clear();
    }
}
